import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

import Icon from '../icon/Icon'
import Text from '../Text'
import { HoverText, IPointerPosition } from '../tag/JobTag'
import { useAppState } from '../../context/AppContext'

type DeltaSize = 'small' | 'regular'

interface IProps {
  current?: number
  previous?: number
  size?: DeltaSize
  noPreviousLabelKey?: string
  className?: string
}

// Renders the change between a current and previous numeric value as a small
// pill with a direction arrow (up = primary, down = primaryDark, flat = no
// arrow). When either value is missing it falls back to a neutral "-" pill
// that surfaces a "no previous data" tooltip on hover. Used by the KPI strip.
const DeltaPill: React.FC<IProps> = ({
  current,
  previous,
  size = 'regular',
  noPreviousLabelKey = 'termEvaluationsProgress.deltaNoPrevious',
  className,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()
  const [pointerPosition, setPointerPosition] = useState<IPointerPosition>({})

  const hasCurrent = typeof current === 'number' && !Number.isNaN(current)
  const hasPrevious = typeof previous === 'number' && !Number.isNaN(previous)

  if (!hasCurrent || !hasPrevious) {
    return (
      <EmptyPill
        className={className}
        $size={size}
        onPointerEnter={(e) =>
          setPointerPosition({
            x: Math.round(e.pageX),
            y: Math.round(e.pageY),
          })
        }
        onPointerLeave={() => setPointerPosition({})}
      >
        <Text textStyle="bodySmall" color="textLight" tag="span">
          -
        </Text>
        {pointerPosition.x && pointerPosition.y && (
          <HoverText text={t(noPreviousLabelKey)} position={pointerPosition} />
        )}
      </EmptyPill>
    )
  }

  const delta = Math.round(current) - Math.round(previous)
  const positive = delta > 0
  const negative = delta < 0

  return (
    <Pill
      className={className}
      $size={size}
      $positive={positive}
      $negative={negative}
    >
      {delta !== 0 && (
        <Arrow $negative={negative}>
          <Icon type="ArrowTopRight" size={12} color={theme.colors.text} />
        </Arrow>
      )}
      <DeltaValue
        textStyle={size === 'small' ? 'caption' : 'labelHeavy'}
        color="text"
        tag="span"
      >
        {delta > 0 ? `+${delta}` : delta}
      </DeltaValue>
    </Pill>
  )
}

export default DeltaPill

const pillBase = css<{ $size: DeltaSize }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${({ $size }) => ($size === 'small' ? '4px 6px' : '4px 8px')};
  border-radius: 8px;
`

const Pill = styled.span<{
  $size: DeltaSize
  $positive: boolean
  $negative: boolean
}>`
  ${pillBase}
  background-color: ${({ theme, $positive, $negative }) =>
    $positive || $negative
      ? theme.colors.primaryLight
      : theme.colors.textLightest};
`

const EmptyPill = styled.span<{ $size: DeltaSize }>`
  ${pillBase}
  background-color: ${({ theme }) => theme.colors.textLightest};
`

const Arrow = styled.span<{ $negative: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: ${({ $negative }) => ($negative ? 'rotate(90deg)' : 'none')};
  transition: transform 0.15s ease;

  > span {
    margin-right: 0;
  }
`

const DeltaValue = styled(Text)`
  line-height: 1;
`
